import { money } from "./quotePricingPolicy";

export function injectPartsTemplate(templateText, card, selectedParts, deliveryPerPart, { conditionLabel, sideLabel }) {
  const customerName = card?.customer?.customer_name || card?.customer_name || "";
  const queryId = card?.query_id || card?.id || "";

  // Substitute placeholder if value is non-empty; otherwise drop the entire line
  const replaceOrDrop = (text, placeholder, value) => {
    if (value) return text.replace(new RegExp(`\\{\\{${placeholder}\\}\\}`, "gi"), value);
    return text.replace(new RegExp(`[^\n]*\\{\\{${placeholder}\\}\\}[^\n]*\n?`, "gi"), "");
  };

  let result = templateText.replace(
    /\{\{#parts\}\}([\s\S]*?)\{\{\/parts\}\}/g,
    (_, block) =>
      selectedParts.map(({ part, quote, sp, vatPct, vatMode, oldExchange }) => {
        const basePrice = (sp ?? Number(quote.quoted_price || 0)) + deliveryPerPart;
        const finalPrice = money(vatMode === "incl" ? basePrice * (1 + vatPct / 100) : basePrice);
        const vatText = vatMode === "incl" ? `(Incl. VAT ${vatPct}%)` : vatMode === "excl" ? `+ VAT ${vatPct}%` : "";
        const priceText = finalPrice.toLocaleString();
        const wd = Number(quote.warranty_days || 0);
        const warrantyText = wd > 0 ? `${wd} days replacement warranty` : "";

        // Appended once, at the very end of the price/VAT line — e.g.
        // "AED 450 + VAT 5% (With Old Exchange)". Guarded so it's added
        // exactly once even if a template uses {{price}} and {{vat}}
        // unpaired (each substituted by a separate replace below).
        let oldExchangeAppended = false;
        const withOldExchangeSuffix = (text) => {
          if (!oldExchange || oldExchangeAppended) return text;
          oldExchangeAppended = true;
          return `${text} (With Old Exchange)`;
        };

        let b = block;
        b = replaceOrDrop(b, "part_name",      part.part_name || "");
        b = replaceOrDrop(b, "side",            sideLabel(part.side || ""));
        b = replaceOrDrop(b, "partnumber",      part.part_number || "");
        b = replaceOrDrop(b, "part_condition",  conditionLabel(part.condition_preference ?? part.condition ?? part.part_condition ?? ""));
        b = replaceOrDrop(b, "qty",             part.quantity ? String(part.quantity) : "");
        // Price + VAT on same line — combine to avoid trailing space when VAT is off
        b = b.replace(
          /\{\{price\}\}(\s*)\{\{vat\}\}/gi,
          (_, sep) => withOldExchangeSuffix(vatText ? `${priceText}${sep}${vatText}` : priceText)
        );
        b = b.replace(/\{\{price\}\}/gi, () => withOldExchangeSuffix(priceText));
        b = b.replace(/\{\{vat\}\}/gi,   () => withOldExchangeSuffix(vatText));
        b = replaceOrDrop(b, "warranty", warrantyText);

        return b;
      }).join("")
  );

  result = result
    .replace(/\{\{name\}\}/gi,          customerName)
    .replace(/\{\{customer_name\}\}/gi, customerName)
    .replace(/\{\{query_id\}\}/gi,      String(queryId))
    .replace(/\{\{query_number\}\}/gi,  String(queryId));

  return result;
}

export function buildQuoteSharePayload({ parts, includedPartIds, pricedById, vatPct, vatMode, deliveryPerPart, partOldExchange, conditionLabel, card, customerPhone, rawType, userId, authState, activeTab, messageText, textManuallyEdited, deliveryEnabled, deliveryAmount, resubmissionReason }) {
    const partsPayload = parts
      .filter(p => p.selected_quote_id)
      .map(p => {
        const quote = (p.supplier_quotes ?? []).find(q => q.id === p.selected_quote_id);
        if (!quote) return null;
        const isIncluded = includedPartIds.has(p.id);
        const calculated = pricedById.get(Number(p.id));
        const sp = calculated.sp;
        const cp = calculated.cp;
        const pct = vatPct;
        const mode = vatMode;
        const share = isIncluded ? deliveryPerPart : 0;
        const basePrice = sp + share;
        const finalPrice = money(mode === "incl" ? basePrice * (1 + pct / 100) : basePrice);
        return {
          query_part_id: p.id,
          part_name: p.part_name || "",
          quote_id: p.selected_quote_id,
          included: isIncluded,
          sp,
          base_sp: calculated.base_sp,
          cp,
          condition: conditionLabel(p.condition_preference ?? p.condition ?? p.part_condition) || "New",
          warranty_days: Number(quote.warranty_days || 0),
          vat_enabled: mode !== "none", // kept for older readers of this payload shape
          vat_mode: mode,
          vat_pct: pct,
          delivery_share: isIncluded ? Number(deliveryPerPart.toFixed(2)) : 0,
          final_price: finalPrice,
          old_exchange: !!partOldExchange[p.id],
        };
      })
      .filter(Boolean);

    return {
      pricing_policy_version: 2,
      query_pk: card?.id,
      query_id: card?.query_id,
      customer_name: card?.customer?.customer_name || card?.customer_name || "",
      customer_phone: customerPhone,
      customer_type: rawType,
      submitted_by_name: authState?.user?.name || authState?.user?.username || "",
      template_type: activeTab,
      message_text: messageText.trim(),
      message_customised: textManuallyEdited,
      delivery_enabled: deliveryEnabled,
      delivery_amount: deliveryEnabled ? deliveryAmount || 0 : 0,
      parts: partsPayload,
      ...(resubmissionReason ? { resubmission_reason: resubmissionReason } : {}),
    };


}
